/**
 * SelectField Component
 *
 * Tyler Forge select adapter for React Hook Form
 */

import { forwardRef } from 'react';
import { type FieldError } from 'react-hook-form';
import '@tylertech/forge/select';
import './SelectField.css';

export interface SelectOption {
  /** Option value */
  value: string;
  /** Option label */
  label: string;
  /** Is option disabled */
  disabled?: boolean;
}

export interface SelectFieldProps {
  /** Field name (required for React Hook Form) */
  name: string;
  /** Field label */
  label?: string;
  /** Select options */
  options: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Is field required */
  required?: boolean;
  /** Is field disabled */
  disabled?: boolean;
  /** Helper text */
  helperText?: string;
  /** Validation error from React Hook Form */
  error?: FieldError;
  /** Default value */
  defaultValue?: string;
  /** Current value (controlled) */
  value?: string;
  /** Change handler */
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Blur handler */
  onBlur?: (event: React.FocusEvent<HTMLSelectElement>) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Select field adapter for React Hook Form
 *
 * Wraps Tyler Forge select component with React Hook Form support
 *
 * @example
 * ```tsx
 * <SelectField
 *   label="Service Type"
 *   options={[
 *     { value: 'Application', label: 'Application' },
 *     { value: 'Integration', label: 'Integration' }
 *   ]}
 *   {...register('serviceType', { required: 'Service type is required' })}
 *   error={errors.serviceType}
 * />
 * ```
 */
const ForgeSelect = 'forge-select' as React.ElementType;
const ForgeOption = 'forge-option' as React.ElementType;

export const SelectField = forwardRef<HTMLElement, SelectFieldProps>(
  (
    {
      name,
      label,
      options,
      placeholder = 'Select an option',
      required = false,
      disabled = false,
      helperText,
      error,
      defaultValue,
      value,
      onChange,
      onBlur,
      className,
    },
    ref
  ) => {
    const hasError = Boolean(error);
    const errorId = hasError ? `${name}-error` : undefined;

    return (
      <div className={`select-field-wrapper ${className || ''}`}>
        <ForgeSelect
          ref={ref}
          id={name}
          name={name}
          label={label || undefined}
          required={required || undefined}
          disabled={disabled || undefined}
          invalid={hasError || undefined}
          value={value}
          defaultValue={defaultValue}
          placeholder={placeholder || undefined}
          onChange={onChange}
          onBlur={onBlur}
          aria-required={required || undefined}
          aria-invalid={hasError ? 'true' : undefined}
          aria-describedby={errorId}
        >
          {options.map((option) => (
            <ForgeOption
              key={option.value}
              value={option.value}
              disabled={option.disabled || undefined}
            >
              {option.label}
            </ForgeOption>
          ))}
          {hasError && error?.message && (
            <div id={errorId} slot="support-text" className="form-error-message" role="alert">
              {error.message}
            </div>
          )}
          {!hasError && helperText && (
            <div slot="support-text" className="form-helper-text">
              {helperText}
            </div>
          )}
        </ForgeSelect>
      </div>
    );
  }
);

SelectField.displayName = 'SelectField';
